import { useEffect } from 'react';
import { GoingFacepile } from '@components/GoingFacepile';
import { selectGoingFaces } from '@components/goingFaces';
import { AttentionDot } from '@components/ui/AttentionDot';
import { enteredThisSession } from '@hooks/useRevealChoreography';
import { cn } from '@lib/utils';
import { FOCUS_RING_CLASSES, PRESS_FEEDBACK_CLASSES, useEnterSettle } from './liveMotion';
import { joinPillSegments, type LivePillGoers, type LivePillSegments } from './livePillSegments';

/** sessionStorage namespace for enter-animation gating — owned by LivePill. */
const ENTER_KEY_PREFIX = 'live-pill-entered:';

/**
 * The goer stack's face-selection rule (pill v2, recap/pill redesign §1):
 * pictures or initials both qualify (a blank picture-less user folds into the
 * +N count). ONE constant feeds both the fallback branch below and the
 * rendered pile, so what decides the branch is exactly what would render.
 */
const PILL_FACES_FILTER = 'picture-or-name' as const;

/**
 * Below this row width the going segment hides so the posts count and a
 * readable name slice survive (T-5 hierarchy: the name ellipsizes first;
 * when both counts still can't fit, going yields, posts stays). Applied ONLY
 * when a posts segment exists to take over — the ruling's premise is "hide
 * going to keep posts", so going never hides when it is the pill's only
 * count (no-posts live pills, every soon pill) — and only on the TEXT
 * fallback branch (the v2 faces form has no going text at all). The 20rem
 * threshold was derived for the v1 metrics (text-sm + the 44px ✕); v2's
 * fallback line is strictly narrower (text-xs, no ✕), so the gate errs
 * toward keeping the name readable — the visual pass owns retuning it.
 * Container-relative on purpose: the pill answers to its row, never the
 * viewport (`@container` lives on the pill's own wrapper below).
 */
const GOING_TIGHT_HIDE_CLASS = '@max-[20rem]:hidden';

const hasEnteredThisSession = (enterKey: string | undefined): boolean =>
  enterKey ? enteredThisSession.read(ENTER_KEY_PREFIX + enterKey) === true : false;

export interface LivePillProps {
  /**
   * One-line pill copy, already composed by the surface (spec §4 copy set),
   * split at its truncation joints (`composePillSegments`): only the name
   * may ellipsize, counts stay visible. The joined line is ALWAYS the pill's
   * accessible name, whatever the visual layer renders (faces or text).
   */
  segments: LivePillSegments;
  /**
   * Goer stack (pill v2, recap/pill redesign §1): when at least one face
   * qualifies, the pile VISUALLY replaces the "is live · {n} going" text —
   * `● [faces +N] {name} · {m} posts`. Absent/empty/none-qualifying (the
   * space PROBE path carries no `preview_users`) → the previous text form
   * renders instead — data-bounded degradation. Compose via
   * `composePillGoers` (live-only). Ignored on `soon` pills: the countdown
   * status is information no dot can carry.
   */
  goers?: LivePillGoers | null;
  /** Soon variant: static yellow dot instead of the pulsing live dot (§4.1). */
  soon?: boolean;
  /** Tap on the pill body — navigate to the event page feed tab (L7). */
  onTap: () => void;
  /**
   * Identity of this appearance; consumers pass `${eventId}:${phase}` (the
   * component owns the storage prefix). When set, the enter animation plays
   * only on genuine first appearance per session — later mounts with the
   * same key render settled. Omit to animate on every mount. The gate is
   * evaluated per mount: when the appearance identity changes on a mounted
   * pill (phase flip), remount it with a React `key`.
   */
  enterKey?: string;
  /**
   * Delays only the ENTER transition by this many ms (default 0), so the
   * W2-3 card→pill fold can stagger the pill ~60ms behind the card exit and
   * read as one folding motion.
   */
  enterDelayMs?: number;
  /**
   * Extra classes cn-merged onto the pill itself — layout knobs per surface.
   * Rows pass `w-full` so pills fill their swipe-row slot (the §0 width
   * rule); standalone pills keep the default `w-fit` content hug.
   */
  className?: string;
  /**
   * Helpable-at-event count (Track 2 Phase A, spec P3): how many posts at THIS
   * live event the viewer can help with, from `useEventHelpable(...).total`.
   * When > 0 a small tinted "I can help" chip is APPENDED as the pill's LAST
   * element - nothing else in the pill moves (the twice-ruled append-only
   * rule). `useEventHelpable` owns the flag + live-window + goers-only gate, so
   * a positive value already means "flag on and real matches"; the pill only
   * asks "> 0?". Omitted / 0 renders no chip, so every existing caller is
   * byte-identical.
   */
  helpableCount?: number;
}

/**
 * Slim ambient pill, v2 shape (recap/pill redesign §1, X-Spaces reference):
 * `● [goer stack +N] {name, truncating} · {m} posts`. The red dot carries
 * "live", the faces carry "who" — the old "is live · {n} going" TEXT renders
 * only as the fallback when no preview face qualifies (space probe path).
 * NO ✕: dismissal ends at the card fold (stage 1); a live-window pill is
 * permanent. `role="status" aria-live="polite"`; the whole body taps through
 * to the event. Motion (§4.1): enter opacity 0→1, translateY(-6px)→0, scale
 * 0.97→1 over 200ms `ease-out-strong` (never from scale(0)); CSS transitions
 * (not keyframes) so rapid state changes retarget. Reduced motion drops the
 * movement and keeps the opacity fade.
 *
 * Truncation hierarchy (T-5, simplified by v2): the NAME (`min-w-0
 * truncate`) is the only part that ellipsizes; the goer stack and the counts
 * are `shrink-0`. On the text-fallback branch, rows tighter than
 * `GOING_TIGHT_HIDE_CLASS` hide the going segment ONLY when a RESOLVED posts
 * segment is present to take over (posts outranks going; a lone going count
 * never hides, and a still-loading posts skeleton (`segments.postsPending`,
 * T-8) never triggers the hide — it may resolve to 0). Assistive tech is
 * exempt from all of it: an sr-only span carries the full joined line — name
 * + live + who + counts (§0 a11y) — and the visual segments (faces included)
 * are aria-hidden, so the status region and the body button always expose
 * every count. That sr line, not a facepile `srLabel`, is the pile's
 * accessible-name duty-holder here — passing `srLabel` too would announce
 * the going count twice.
 */
export function LivePill({
  segments,
  goers,
  soon = false,
  onTap,
  enterKey,
  enterDelayMs = 0,
  className,
  helpableCount,
}: LivePillProps) {
  const settled = useEnterSettle(() => hasEnteredThisSession(enterKey), enterDelayMs);

  // Record the appearance (external-system write) so revisits within this
  // session render settled instead of re-playing the enter.
  useEffect(() => {
    if (!enterKey) {
      return;
    }
    enteredThisSession.write(true, ENTER_KEY_PREFIX + enterKey);
  }, [enterKey]);

  // The faces-vs-text branch (§1 fallback rule): decided by the SAME selection
  // the pile renders, so an all-filtered preview can never leave an empty gap.
  // Soon pills always take the text branch — their countdown status is the
  // information, and no dot carries it.
  const faces = !soon && goers ? selectGoingFaces(goers.users, PILL_FACES_FILTER) : [];
  const showFaces = faces.length > 0;

  return (
    // The pill's own container context: the going segment's tight-row hide
    // queries THIS wrapper's width (the row the pill actually lives in), so
    // the behavior follows the surface's real layout, never the viewport.
    <div className="@container">
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'flex w-fit max-w-full items-center rounded-full border border-gray-100 bg-white px-2.5 py-1 shadow-soft',
          'transition-[opacity,translate,scale]',
          settled
            ? 'translate-y-0 scale-100 opacity-100 duration-200 ease-out-strong'
            : '-translate-y-1.5 scale-[0.97] opacity-0 duration-200 ease-out-strong motion-reduce:translate-y-0 motion-reduce:scale-100',
          className,
        )}
      >
        <button
          type="button"
          onClick={onTap}
          className={cn(
            // -my-1 cancels the container's py-1 so the tap target spans the full
            // pill height (~40px) while the rendered pill stays visually slim
            // (§4.1 hit areas). w-full so a row-stretched pill is tappable
            // edge to edge; inside the default w-fit it is content-sized anyway.
            'flex w-full min-w-0 items-center gap-1.5 rounded-full -my-1 py-2.5 text-left',
            PRESS_FEEDBACK_CLASSES,
            FOCUS_RING_CLASSES,
          )}
        >
          <AttentionDot
            className={cn(
              'h-2 w-2',
              soon ? 'bg-mb-yellow' : 'bg-mb-live animate-live-pulse motion-reduce:animate-none',
            )}
          />
          {/* The full line for assistive tech — visual truncation/hiding/faces never touch it. */}
          <span className="sr-only">{joinPillSegments(segments)}</span>
          {showFaces && goers ? (
            // Decorative compression (§0): the count lives in the sr line above.
            <span aria-hidden="true" className="shrink-0">
              <GoingFacepile
                users={goers.users}
                faceSizeClasses="h-5 w-5"
                ringClass="ring-white"
                filter={PILL_FACES_FILTER}
                totalCount={goers.totalCount}
                overlapClasses="-space-x-1.5"
              />
            </span>
          ) : null}
          {/* min-h-5 keeps text-fallback pills the same height as faces pills,
              so mixed rows sit on one line. */}
          <span
            aria-hidden="true"
            className="flex min-h-5 min-w-0 items-center text-xs font-medium text-gray-900 tabular-nums"
          >
            <span className="min-w-0 truncate">{segments.name}</span>
            {/* Tail segments carry their leading spaces; whitespace-pre keeps them
                so the joints render exactly as the single string did. With faces
                shown, status + going render as faces, not text (§1 shape). */}
            {showFaces ? null : (
              <>
                <span className="shrink-0 whitespace-pre">{segments.status}</span>
                {segments.going !== null ? (
                  <span
                    className={cn(
                      'shrink-0 whitespace-pre',
                      // Tight-row hide only when posts exists to take over — going
                      // must never vanish as the pill's only count.
                      segments.posts !== null && GOING_TIGHT_HIDE_CLASS,
                    )}
                  >
                    {segments.going}
                  </span>
                ) : null}
              </>
            )}
            {segments.posts !== null ? (
              <span className="shrink-0 whitespace-pre">{segments.posts}</span>
            ) : segments.postsPending ? (
              // Posts count still loading (T-8): a small shimmer sized like
              // `· 8 posts`, same shrink-0 treatment as a resolved tail so the
              // NAME still ellipsizes first. Deliberately not a "posts exists"
              // signal — the going tight-row hide above keys on a RESOLVED
              // `posts` only, because a pending count may resolve to 0 and
              // going must never have hidden for nothing. Inside the
              // aria-hidden visual line, so assistive tech never hears it.
              <span className="flex shrink-0 items-center whitespace-pre">
                {' · '}
                <span className="h-3 w-11 animate-pulse rounded bg-gray-200" />
              </span>
            ) : null}
          </span>
          {helpableCount != null && helpableCount > 0 ? (
            // Helpable chip (spec P3, locked form F): the exact "I can help"
            // icon asset the ForYouSection header uses + the count, in the
            // helpable-green chip family. APPENDED as the button's LAST child -
            // nothing above it moves - so the whole pill still taps through to
            // the event (the chip is not separately interactive in Phase A).
            // `shrink-0` keeps it out of the truncation shrink path: the name
            // (`min-w-0 truncate`) still ellipsizes first and the chip is the
            // last to yield (it is the point). Icon + number are decorative;
            // the sr-only span carries the words - the pill's own
            // compress-visual / full-sr-line pattern, so the count joins the
            // button's accessible name after the sr line above.
            <span className="flex shrink-0 items-center gap-1 rounded-full border border-green-200 bg-green-50 px-1.5 py-0.5 text-xs font-semibold text-green-700 tabular-nums">
              <img src="/I Can Help.svg" alt="" aria-hidden="true" className="h-3.5 w-3.5" />
              <span aria-hidden="true">{helpableCount}</span>
              <span className="sr-only">
                {` · ${helpableCount} ${helpableCount === 1 ? 'post' : 'posts'} you can help with`}
              </span>
            </span>
          ) : null}
        </button>
      </div>
    </div>
  );
}
